/** Get raw part config objects from MachineConfig state or JSON strings. */
import type { JsonObject, JsonValue } from '@protobuf-ts/runtime';
import type { ZodError } from 'zod';

import {
  ConfigErrorTypes,
  type ConfigSemanticError,
  safeParseJSONObject,
  SemanticErrorLevel,
} from '$lib/parse-config';

import * as ConfigItem from './config-item';
import * as Schemas from './config-schemas';
import {
  isInvalidMachineConfigPart,
  type ReadonlyMachineConfig,
} from './machine-config';
import * as RawResources from './raw-resources';

/** Given normalized config and part ID, return the part's raw config object. */
export const getRawPartConfig = (
  config: ReadonlyMachineConfig,
  partID: string | undefined
): JsonValue | string => {
  const part = partID ? config.parts[partID] : undefined;
  if (part === undefined) {
    return {};
  }
  if (isInvalidMachineConfigPart(part)) {
    if (typeof part.config === 'string') {
      return part.config;
    }
    return part.config as JsonValue;
  }

  const rawResourceParent = toRawResourceParent(part);
  // eslint-disable-next-line camelcase
  const fragment_mods = RawResources.toRawFragmentMods(part.fragmentMods);

  return {
    ...rawResourceParent,
    // eslint-disable-next-line camelcase
    ...(fragment_mods.length > 0 ? { fragment_mods } : undefined),
    ...part.otherProps,
  } as JsonValue;
};

/** Full machine config as raw JSON: a single part's config, or parts keyed by name. */
export const getRawMachineConfig = (
  config: ReadonlyMachineConfig
): JsonObject => {
  const partIDs = [config.mainID, ...config.subIDs].filter(
    (id): id is string => id !== undefined
  );

  if (partIDs.length <= 1) {
    const raw = getRawPartConfig(config, partIDs[0]);
    return (typeof raw === 'string' ? JSON.parse(raw) : raw) as JsonObject;
  }

  const combined: Record<string, JsonValue> = {};
  for (const partID of partIDs) {
    const raw = getRawPartConfig(config, partID);
    combined[config.parts[partID]?.name ?? partID] = (
      typeof raw === 'string' ? JSON.parse(raw) : raw
    ) as JsonValue;
  }
  return combined;
};

const toRawResourceParent = (
  resourceParent: ConfigItem.ReadonlyResourceParent
): JsonObject => {
  const agent = RawResources.toRawAgent(resourceParent.agent);
  const billing = RawResources.toRawBilling(resourceParent.billing);
  const network = RawResources.toRawNetwork(resourceParent.network);
  const maintenance = RawResources.toRawMaintenance(resourceParent.maintenance);
  const components = deNormalize(
    resourceParent.components,
    RawResources.toRawComponent
  );
  const services = deNormalize(
    resourceParent.services,
    RawResources.toRawService
  );
  const modules = [
    ...deNormalize(
      resourceParent.registryModules,
      RawResources.toRawRegistryModule
    ),
    ...deNormalize(resourceParent.localModules, RawResources.toRawLocalModule),
  ];
  const processes = deNormalize(
    resourceParent.processes,
    RawResources.toRawProcess
  );
  const packages = deNormalize(
    resourceParent.packages,
    RawResources.toRawPackage
  );
  const remotes = deNormalize(resourceParent.remotes, RawResources.toRawRemote);
  const triggers = deNormalize(resourceParent.triggers, (resource) =>
    RawResources.toRawTrigger(resource, resourceParent.components)
  );
  const jobs = deNormalize(resourceParent.jobs, RawResources.toRawJob);
  const fragments = deNormalize(
    resourceParent.fragmentImports,
    RawResources.toRawFragmentImport
  );

  return {
    ...(agent && { agent }),
    ...(billing && { billing }),
    ...(network && { network }),
    ...(maintenance && { maintenance }),
    ...(components.length > 0 ? { components } : undefined),
    ...(services.length > 0 ? { services } : undefined),
    ...(modules.length > 0 ? { modules } : undefined),
    ...(processes.length > 0 ? { processes } : undefined),
    ...(packages.length > 0 ? { packages } : undefined),
    ...(remotes.length > 0 ? { remotes } : undefined),
    ...(triggers.length > 0 ? { triggers } : undefined),
    ...(jobs.length > 0 ? { jobs } : undefined),
    ...(fragments.length > 0 ? { fragments } : undefined),
  } as JsonObject;
};

/** De-normalize a record of resource configs to a list. */
const deNormalize = <T, R>(
  resources: Record<ConfigItem.ResourceID, T>,
  mapper: (resource: T) => R
): R[] => {
  return Object.values(resources).map((resource) => mapper(resource));
};

export interface ConfigParseSemanticError {
  success: false;
  errorType: typeof ConfigErrorTypes.SEMANTIC;
  errors: ConfigSemanticError[];
  // TODO(ehhong): type this as JsonObject or JsonValue
  data: unknown;
}

export interface ConfigParseSyntacticError {
  success: false;
  errorType: typeof ConfigErrorTypes.SYNTAX;
  error: string;
  data: string;
}
export type ConfigParseError =
  | ConfigParseSemanticError
  | ConfigParseSyntacticError;

export interface ConfigParseSuccess<T> {
  success: true;
  /** The parsed data. */
  data: T;
  /** Whether the config has a non-fatal semantic error. */
  errorType?: typeof ConfigErrorTypes.SEMANTIC | undefined;
  /** Any non-fatal semantic errors. */
  errors?: ConfigSemanticError[] | undefined;
}

export type ConfigParseResult<T> = ConfigParseSuccess<T> | ConfigParseError;
export type ConfigSemanticParseResult<T> =
  | ConfigParseSuccess<T>
  | ConfigParseSemanticError;

export const zodToSemanticErrors = (
  zodError: ZodError,
  transform?: (error: ConfigSemanticError) => ConfigSemanticError
): ConfigSemanticError[] => {
  return zodError.issues.map((zodIssue) => {
    const semanticError = {
      type: ConfigErrorTypes.SEMANTIC,
      path: zodIssue.path,
      message: zodIssue.message,
      level: SemanticErrorLevel.ERROR,
      disablesBuilder: true,
    };
    return transform ? transform(semanticError) : semanticError;
  });
};

export const semanticallyParseRawPartConfig = (
  rawPartConfig: JsonValue
): ConfigSemanticParseResult<Schemas.RawPartConfig> => {
  const result = Schemas.RawPartConfigSchema.safeParse(rawPartConfig);

  if (!result.success) {
    return {
      success: false,
      errorType: ConfigErrorTypes.SEMANTIC,
      errors: zodToSemanticErrors(result.error),
      data: rawPartConfig as Record<string, unknown>,
    };
  }

  return {
    success: true,
    data: result.data,
  };
};

/**
 * Parse a JSON string of a part config.
 *
 * Returns `SchemaParseSyntaxError` if string is not a syntactically valid config.
 * Returns `SchemaParseSemanticError` if string is not a semantically valid config.
 * Returns `SchemaParseSuccess` if string is a valid config.
 * When needed, this function should be updated to return
 * structured validation issues instead.
 */
export const parseRawPartConfig = (
  value: string
): ConfigParseResult<Schemas.RawPartConfig> => {
  const syntaxParseResult = safeParseJSONObject(value);

  return syntaxParseResult.success
    ? semanticallyParseRawPartConfig(syntaxParseResult.data)
    : syntaxParseResult;
};

export const fromRawFragmentConfig = (
  fragmentConfig: Schemas.RawFragmentConfig,
  fragmentID: string,
  prefix?: string
): ConfigItem.FragmentConfig => {
  const {
    description,
    agent,
    billing,
    network,
    maintenance,
    components = [],
    services = [],
    modules = [],
    remotes = [],
    processes = [],
    packages = [],
    triggers = [],
    jobs = [],
    fragments = [],
    ...otherProps
  } = fragmentConfig;

  const parentID = `${prefix ? `${prefix}-` : ''}${fragmentID}`;

  return {
    description,
    otherProps,
    ...RawResources.fromRawResourceParent(
      {
        agent,
        billing,
        network,
        maintenance,
        components,
        services,
        modules,
        remotes,
        processes,
        packages,
        triggers,
        jobs,
        fragments,
      },
      parentID
    ),
  };
};

export const toRawFragmentConfig = (
  fragmentConfig: ConfigItem.FragmentConfig
): JsonObject => {
  const description = fragmentConfig.description;
  return {
    ...toRawResourceParent(fragmentConfig),
    ...(description && { description }),
    ...fragmentConfig.otherProps,
  };
};
